import React, { useEffect, useState } from "react";
import { createDispute } from "@/services/disputes.service";
import { listContracts, getContract } from "@/services/contracts.service";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { fmtMoney } from "@/lib/format";

const REASONS = [
  "Work not delivered",
  "Quality issue",
  "Scope mismatch",
  "Late delivery",
  "Non-payment",
  "Other",
];

export const FileDisputeModal = ({ open, onClose, onFiled, prefillContractId = null, prefillMilestoneId = null }) => {
  const [contracts, setContracts] = useState([]);
  const [contractId, setContractId] = useState("");
  const [milestones, setMilestones] = useState([]);
  const [milestoneId, setMilestoneId] = useState("");
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [amount, setAmount] = useState("");
  const [outcome, setOutcome] = useState("Other");
  const [evidence, setEvidence] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const cs = await listContracts();
        setContracts(cs.filter((c) => c.status === "Active" || c.unified_status === "In dispute"));
      } catch {}
    })();
  }, [open]);

  useEffect(() => {
    if (open && prefillContractId) setContractId(prefillContractId);
  }, [open, prefillContractId]);

  useEffect(() => {
    if (!contractId) { setMilestones([]); return; }
    (async () => {
      try {
        const c = await getContract(contractId);
        const disputable = c.milestones.filter((m) => ["Funded", "Submitted", "Approved"].includes(m.status));
        setMilestones(disputable);
        if (prefillMilestoneId && disputable.some((m) => m.milestone_id === prefillMilestoneId)) {
          setMilestoneId(prefillMilestoneId);
        }
      } catch {}
    })();
  }, [contractId, prefillMilestoneId]);

  useEffect(() => {
    if (!milestoneId) return;
    const m = milestones.find((x) => x.milestone_id === milestoneId);
    if (m) setAmount(String(m.amount));
  }, [milestoneId, milestones]);

  const reset = () => {
    setContractId(""); setMilestoneId(""); setReason(""); setDetails("");
    setAmount(""); setOutcome("Other"); setEvidence("");
  };

  const submit = async () => {
    if (!milestoneId) { toast.error("Select a milestone"); return; }
    if (!reason) { toast.error("Select a reason"); return; }
    if (!details) { toast.error("Add some details"); return; }
    setSaving(true);
    try {
      await createDispute({
        milestone_id: milestoneId, reason, details,
        amount_in_dispute: amount ? parseFloat(amount) : null,
        desired_outcome: outcome,
        evidence_link: evidence || null,
      });
      toast.success("Dispute filed");
      reset();
      onClose();
      onFiled && onFiled();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to file dispute");
    } finally {
      setSaving(false);
    }
  };

  const selectedMs = milestones.find((m) => m.milestone_id === milestoneId);
  const sc = contracts.find((c) => c.contract_id === contractId);
  const payeeName = sc ? (sc.is_payee ? "me" : sc.freelancer_name || sc.freelancer_email || "the payee") : "the payee";
  const payerName = sc ? (sc.is_payer ? "me" : sc.business_name || "the payer") : "the payer";
  const OUTCOMES = [
    { v: "ReleaseToPayee", l: `Release funds to ${payeeName}` },
    { v: "RefundToPayer", l: `Refund to ${payerName}` },
    { v: "Split", l: "Split between parties" },
    { v: "Other", l: "Other" },
  ];

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { reset(); onClose(); } }}>
      <DialogContent className="max-w-xl" data-testid="file-dispute-modal">
        <DialogHeader><DialogTitle>File a dispute</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Contract *</Label>
            <select value={contractId} onChange={(e) => { setContractId(e.target.value); setMilestoneId(""); }} className="mt-1.5 h-11 w-full rounded-md border border-input bg-background px-3 text-sm accent-primary transition-colors focus:outline-none focus:ring-1 focus:ring-ring focus:border-ring" data-testid="dispute-contract-select">
              <option value="">Select contract…</option>
              {contracts.map((c) => (
                <option key={c.contract_id} value={c.contract_id}>{c.title} · {fmtMoney(c.total_amount, c.currency)}</option>
              ))}
            </select>
          </div>
          {contractId && (
            <div>
              <Label>Milestone *</Label>
              <select value={milestoneId} onChange={(e) => setMilestoneId(e.target.value)} className="mt-1.5 h-11 w-full rounded-md border border-input bg-background px-3 text-sm accent-primary transition-colors focus:outline-none focus:ring-1 focus:ring-ring focus:border-ring" data-testid="dispute-milestone-select">
                <option value="">Select milestone…</option>
                {milestones.map((m) => (
                  <option key={m.milestone_id} value={m.milestone_id}>{m.title} · {m.status} · {fmtMoney(m.amount)}</option>
                ))}
              </select>
              {milestones.length === 0 && (
                <p className="mt-1 text-xs text-muted-foreground">No disputable milestones on this contract (must be Funded / Submitted / Approved).</p>
              )}
            </div>
          )}
          <div>
            <Label>Reason *</Label>
            <select value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1.5 h-11 w-full rounded-md border border-input bg-background px-3 text-sm accent-primary transition-colors focus:outline-none focus:ring-1 focus:ring-ring focus:border-ring" data-testid="dispute-reason-select">
              <option value="">Select…</option>
              {REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div>
            <Label>Details *</Label>
            <Textarea value={details} onChange={(e) => setDetails(e.target.value)} rows={4} className="mt-1.5" placeholder="Give specifics so a reviewer can act fast." data-testid="dispute-details-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Amount in dispute</Label>
              <Input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={selectedMs ? String(selectedMs.amount) : "0.00"} className="mt-1.5 h-11" data-testid="dispute-amount-input" />
            </div>
            <div>
              <Label>Desired outcome</Label>
              <select value={outcome} onChange={(e) => setOutcome(e.target.value)} className="mt-1.5 h-11 w-full rounded-md border border-input bg-background px-3 text-sm accent-primary transition-colors focus:outline-none focus:ring-1 focus:ring-ring focus:border-ring" data-testid="dispute-outcome-select">
                {OUTCOMES.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
              </select>
            </div>
          </div>
          <div>
            <Label>Evidence link (optional)</Label>
            <Input value={evidence} onChange={(e) => setEvidence(e.target.value)} placeholder="https://drive.google.com/…" className="mt-1.5 h-11" data-testid="dispute-evidence-input" />
            <p className="mt-1 text-[11px] text-muted-foreground">Drop a link to screenshots, exported files, or a shared folder.</p>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={saving} data-testid="dispute-submit-btn">
            {saving ? "Filing…" : "File dispute"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default FileDisputeModal;
